import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  ChartLineUp, Robot, Trophy, Lightning, Shield, GitFork,
  ArrowRight, ChartBar, Sparkle, Globe,
} from "@phosphor-icons/react";

export default function LandingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [featured, setFeatured] = useState(null);
  const [trending, setTrending] = useState([]);

  // Authenticated users skip the marketing page and go straight into the app.
  useEffect(() => {
    if (user) nav("/dashboard", { replace: true });
  }, [user, nav]);

  // Pull live proof-of-life data — even the landing page runs on real numbers.
  useEffect(() => {
    Promise.all([
      api.get("/leaderboard/featured?days=7").then((r) => r.data.featured).catch(() => null),
      api.get("/leaderboard/trending?days=7&limit=5").then((r) => r.data.symbols).catch(() => []),
    ]).then(([f, t]) => {
      setFeatured(f);
      setTrending(t || []);
    });
  }, []);

  if (user) return null;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="landing-page">
      {/* Nav */}
      <header className="border-b border-zinc-900 sticky top-0 backdrop-blur-lg bg-black/70 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-7 h-7 flex items-center justify-center bg-[#00C805] rounded-sm">
              <span className="text-black font-black">S</span>
            </div>
            <span className="heading font-black tracking-tighter text-lg">STRATOS</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 mono text-[10px] uppercase tracking-widest text-zinc-400">
            <a href="#how-it-works" className="hover:text-white">How it works</a>
            <a href="#community" className="hover:text-white">Community</a>
            <a href="#features" className="hover:text-white">Features</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              data-testid="landing-login-btn"
              className="mono text-[10px] uppercase tracking-widest text-zinc-300 hover:text-white px-3 py-2 rounded-sm"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              data-testid="landing-register-btn"
              className="mono text-[10px] uppercase tracking-widest text-black bg-[#00C805] hover:bg-[#00b004] px-4 py-2 rounded-sm font-semibold"
            >
              Start free →
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-16">
        <div className="mono text-[10px] text-[#00C805] uppercase tracking-widest flex items-center gap-2 mb-6">
          <Lightning size={12} weight="fill" /> Paper trading · 100% risk-free · No credit card
        </div>
        <h1 className="heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl">
          Automated stock strategies,
          <br />
          <span className="text-[#00C805]">without the burnout.</span>
        </h1>
        <p className="text-base sm:text-lg text-zinc-400 mt-8 max-w-2xl leading-relaxed">
          Build bots that trade RSI, MACD, Bollinger Bands and MA crossovers on real market data
          — then compare them against every strategy the community is running. All in a $100k
          paper account, no real money at risk.
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-10">
          <Link
            to="/register"
            data-testid="landing-hero-cta"
            className="inline-flex items-center gap-2 text-sm text-black bg-[#00C805] hover:bg-[#00b004] px-5 py-3 rounded-sm mono text-[11px] uppercase tracking-widest font-semibold"
          >
            Deploy your first bot <ArrowRight size={14} weight="bold" />
          </Link>
          <Link
            to="/leaderboard"
            className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white border border-zinc-800 hover:border-zinc-500 px-5 py-3 rounded-sm mono text-[11px] uppercase tracking-widest"
          >
            <Globe size={14} /> Browse the community
          </Link>
        </div>

        {/* Live proof */}
        {(featured || trending.length > 0) && (
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="landing-live-proof">
            {featured && (
              <Link
                to={`/b/${featured.bot_id}`}
                className="lg:col-span-2 group border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-zinc-900 to-zinc-900 rounded-sm p-6 hover:border-amber-500/60 transition-colors"
              >
                <div className="mono text-[10px] text-amber-300 uppercase tracking-widest flex items-center gap-2">
                  <Trophy size={12} weight="fill" /> Featured this week · live from the community
                </div>
                <div className="flex items-baseline gap-3 mt-3 flex-wrap">
                  <span className="text-3xl font-bold">{featured.symbol}</span>
                  <span className="mono text-xs text-zinc-500 uppercase tracking-widest">
                    {featured.strategy}
                  </span>
                  <span className="mono text-xs text-zinc-400">by {featured.owner_tag}</span>
                </div>
                <div className="mt-4 flex items-baseline gap-4">
                  <span className="mono text-3xl font-bold profit">
                    {fmtMoney(featured.featured_pl, { showSign: true })}
                  </span>
                  <span className="text-xs text-zinc-500">
                    realized in the last {featured.featured_window_days} days · {fmtPct(featured.win_rate)} win rate
                  </span>
                </div>
                <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mt-4 flex items-center gap-1 group-hover:text-white">
                  See the strategy <ArrowRight size={10} />
                </div>
              </Link>
            )}
            {trending.length > 0 && (
              <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5">
                <div className="mono text-[10px] text-zinc-400 uppercase tracking-widest">
                  Trending symbols · 7d
                </div>
                <div className="mt-4 space-y-3">
                  {trending.slice(0, 5).map((t, idx) => (
                    <div key={t.symbol} className="flex items-center justify-between border-b border-zinc-800/60 pb-2 last:border-b-0 last:pb-0">
                      <div className="flex items-center gap-3">
                        <span className="mono text-[10px] text-zinc-500">#{idx + 1}</span>
                        <span className="font-semibold">{t.symbol}</span>
                      </div>
                      <span className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
                        {t.copies} {t.copies === 1 ? "copy" : "copies"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-16 border-t border-zinc-900">
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">What you get</div>
        <h2 className="heading text-3xl sm:text-4xl font-black tracking-tighter mt-3 max-w-2xl">
          Everything Robinhood won't let you build.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {[
            { Icon: Robot, title: "4 built-in strategies", body: "RSI, MACD, Bollinger, MA crossover. Tune the parameters, deploy, and watch the bot execute on real market data." },
            { Icon: ChartBar, title: "Backtest before you deploy", body: "Every bot has a 1-click backtest against the last year of price history — see how it would have performed before staking a paper dollar." },
            { Icon: ChartLineUp, title: "Compare vs. buy-and-hold", body: "Diff 2-5 of your bots side-by-side. Every strategy line ships with a dashed buy-and-hold overlay so you can see if trading actually beat doing nothing." },
            { Icon: Trophy, title: "Community leaderboard", body: "Anonymised rankings of every bot on the platform. Copy the top performers in one tap, tweak, and try to out-trade them." },
            { Icon: GitFork, title: "Copy-with-tweak", body: "Cloning a strategy pre-fills every parameter into your workspace with a Vary ±25% button. No blind cloning — your bots stay yours." },
            { Icon: Shield, title: "Paper only. Always.", body: "$100k in fake cash on day one. No account linking, no funds at risk, no anxiety while you learn." },
          ].map(({ Icon, title, body }) => (
            <div
              key={title}
              data-testid={`feature-${title.split(" ")[0].toLowerCase()}`}
              className="p-5 border border-zinc-800 rounded-sm bg-zinc-900/30 hover:bg-zinc-900/60 transition-colors"
            >
              <Icon size={22} className="text-[#00C805]" />
              <div className="font-semibold mt-3">{title}</div>
              <div className="text-xs text-zinc-400 mt-2 leading-relaxed">{body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="max-w-6xl mx-auto px-6 py-16 border-t border-zinc-900">
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">How it works</div>
        <h2 className="heading text-3xl sm:text-4xl font-black tracking-tighter mt-3 max-w-2xl">
          From idea to executing bot in three moves.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {[
            { step: "01", title: "Pick a strategy", body: "RSI, MACD, Bollinger, or MA Crossover. Set the symbol, quantity, and the parameter that matters." },
            { step: "02", title: "Backtest it", body: "One click runs the strategy over the last year of price history so you know what you're deploying." },
            { step: "03", title: "Watch it trade", body: "The scheduler evaluates every 2 minutes on daily bars. You get email alerts, an equity curve, and a leaderboard rank." },
          ].map(({ step, title, body }) => (
            <div key={step} className="border-l-2 border-[#00C805] pl-5">
              <div className="mono text-[10px] text-[#00C805] uppercase tracking-widest">Step {step}</div>
              <div className="text-xl font-bold mt-2">{title}</div>
              <div className="text-xs text-zinc-400 mt-2 leading-relaxed">{body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Community */}
      <section id="community" className="max-w-6xl mx-auto px-6 py-16 border-t border-zinc-900">
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Community</div>
        <h2 className="heading text-3xl sm:text-4xl font-black tracking-tighter mt-3 max-w-2xl">
          Learn by copying — then out-trade the strategy you copied.
        </h2>
        <p className="text-base text-zinc-400 mt-6 max-w-2xl leading-relaxed">
          Every public bot has a shareable profile with a live price chart, its full parameter set,
          and a plain-English AI explanation of what it does. Copy it, twist the params, and STRATOS
          will ping the original owner when your version starts beating theirs.
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-10">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 text-sm text-black bg-[#00C805] hover:bg-[#00b004] px-5 py-3 rounded-sm mono text-[11px] uppercase tracking-widest font-semibold"
          >
            <Sparkle size={14} weight="fill" /> Create free account
          </Link>
          <Link
            to="/leaderboard"
            className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white border border-zinc-800 hover:border-zinc-500 px-5 py-3 rounded-sm mono text-[11px] uppercase tracking-widest"
          >
            <Globe size={14} /> See who's winning
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 py-8">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between flex-wrap gap-4 mono text-[10px] uppercase tracking-widest text-zinc-500">
          <div>STRATOS · paper trading platform · not financial advice</div>
          <div className="flex items-center gap-4">
            <Link to="/leaderboard" className="hover:text-white">Leaderboard</Link>
            <Link to="/login" className="hover:text-white">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
